import { Box, Drawer, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Button } from "../../../common";
import { CALENDAR_DATE_FORMAT } from "../../../../constants";
import {
  AccountBasicInfo,
  CalendarSlot,
  CrewMember,
  DropdownItem,
  Job,
  Truck,
} from "../../../../types";
import { JobModel } from "../../../../models";
import { theme } from "../../../../utils";
import { JobForm } from "./JobForm";

const JOB_FORM_ID = "jobForm";

type JobDrawerProps = {
  open: boolean;
  job?: Job;
  slot?: CalendarSlot;
  jobs: Job[];
  accounts: AccountBasicInfo[];
  accountDropdown: DropdownItem[];
  trucks: Truck[];
  crew: CrewMember[];
  canWrite: boolean;
  canDelete: boolean;
  isSaving: boolean;
  onSave: (job: Job) => void;
  onDelete: (job: Job) => void;
  onClose: () => void;
};

export const JobDrawer = ({
  open,
  job,
  slot,
  jobs,
  accounts,
  accountDropdown,
  trucks,
  crew,
  canWrite,
  canDelete,
  isSaving,
  onSave,
  onDelete,
  onClose,
}: JobDrawerProps) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: { xs: "100%", sm: 520 },
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 2, md: 3 },
        py: 1.5,
        borderBottom: "1px solid var(--dashboard-border-divider)",
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
        {job
          ? `Edit ${JobModel.getCategoryLabel(job)} — ${JobModel.getJobTitle(job)}`
          : "Schedule"}
      </Typography>
      <IconButton aria-label="Close" onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </Box>

    <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, md: 3 } }}>
      {open && slot && (
        <JobForm
          key={
            job
              ? `job-${job.id}`
              : `slot-${slot.date.format(CALENDAR_DATE_FORMAT)}-${slot.startTime}-${slot.truckId ?? ""}-${slot.crewId ?? ""}`
          }
          id={JOB_FORM_ID}
          defaultValues={job}
          handleEdit={onSave}
          slot={slot}
          jobs={jobs}
          accounts={accounts}
          accountDropdown={accountDropdown}
          trucks={trucks}
          crew={crew}
        />
      )}
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 2, md: 3 },
        py: 2,
        borderTop: "1px solid var(--dashboard-border-divider)",
      }}
    >
      {job && canDelete && (
        <Tooltip title="Delete job">
          <IconButton
            aria-label="Delete job"
            disabled={isSaving}
            onClick={() => onDelete(job)}
          >
            <DeleteOutlineOutlinedIcon
              sx={{ color: isSaving ? undefined : theme.palette.error.main }}
            />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ flex: 1 }} />
      <Button label="Cancel" variantType="secondary" onClick={onClose} />
      {canWrite && (
        <Button
          label={job ? "Save Changes" : "Schedule"}
          variantType="primary"
          type="submit"
          form={JOB_FORM_ID}
          disabled={isSaving}
        />
      )}
    </Box>
  </Drawer>
);
